import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
  type NodeTypes,
} from '@xyflow/react';
import { Text } from '@cotera/client/app/components/ui';
import { Canvas } from '@cotera/client/v0/components/ai-elements/canvas';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useWorkflowRunModel } from './workflow-run.model';
import { runGraphToFlow } from './workflow-run-graph-to-flow';
import {
  WorkflowRunGraphInspector,
  type WorkflowRunInspectorStep,
} from './workflow-run-graph-inspector';
import { useRevealNodeBesideInspector } from './hooks/use-reveal-node-beside-inspector';
import { asRecord, readString } from './components/utils';
import {
  WORKFLOW_EDGE_OPTIONS,
  useSelectedNodeIds,
  withSelectedEdgeHighlight,
} from './workflow-edge-style';
import {
  buildRunStatusByFlowId,
  withRunProgressEdges,
} from './workflow-run-edge-style';
import {
  WORKFLOW_RUN_FLOW_NODE_TYPE,
  WorkflowRunFlowNode,
} from './workflow-run-flow-node';
import {
  DEFAULT_WORKFLOW_RENDER_CATALOG,
  WorkflowRenderCatalogProvider,
} from './workflow-render-catalog';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { WORKFLOW_RUN_STEP_VIEW_TYPE } from './workflow-artifact-views';
import type { WorkflowRunFlowNodeData } from './workflow-run-flow-node';
import { WORKFLOW_FLOW_FIT_VIEW_OPTIONS } from './workflow-flow-layout.constants';
import {
  buildFlowMeasurementKey,
  useMeasuredFlowLayout,
} from './hooks/use-measured-flow-layout';

const { WorkflowNodeType } = PersistedWorkflow;

const isAgentRunNode = (node: Node): boolean => {
  const data = node.data as WorkflowRunFlowNodeData | undefined;
  const nodeType = data?.node.type;
  return (
    nodeType === WorkflowNodeType.Agent ||
    nodeType === WorkflowNodeType.AdHocAgent
  );
};

const WORKFLOW_RUN_NODE_TYPES: NodeTypes = {
  [WORKFLOW_RUN_FLOW_NODE_TYPE]: WorkflowRunFlowNode,
};

/** Re-fit when the laid-out graph changes so small graphs don't sit at ~2× zoom. */
const RunGraphFitView: React.FC<{ layoutKey: string }> = ({ layoutKey }) => {
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (layoutKey === '') {
      return;
    }
    const frame = requestAnimationFrame(() => {
      void fitView(WORKFLOW_FLOW_FIT_VIEW_OPTIONS);
    });
    return () => cancelAnimationFrame(frame);
  }, [layoutKey, fitView]);
  return null;
};

// No `isRunning` here any more: the per-step statuses in `graph.nodes` say which
// arrows are moving, and they say it per leg rather than for the whole run.
const WorkflowRunGraphInner: React.FC<{
  workflowId: string;
  runId: string;
  graph: ReturnType<typeof runGraphToFlow>;
}> = ({ workflowId, runId, graph }) => {
  const artifactPanel = useArtifactPanel();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(graph.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(graph.edges);

  // Each arrow is painted by the step it leaves: solid once that step completed,
  // dashed while it runs, resting until it starts. Keyed off `graph.nodes` (the
  // freshly polled statuses) rather than the `nodes` render state, so the arrows
  // move with the run instead of waiting for the layout pass.
  const statusByFlowId = useMemo(
    () => buildRunStatusByFlowId(graph.nodes),
    [graph.nodes]
  );

  // Selecting a node lights up its incoming and outgoing arrows too, so a step's
  // place in the graph stays readable. Render-time only: neither the progress
  // paint nor the highlight is written back into edge state.
  const selectedNodeIds = useSelectedNodeIds(nodes);
  const renderEdges = useMemo(
    () =>
      withSelectedEdgeHighlight(
        withRunProgressEdges(edges, statusByFlowId),
        selectedNodeIds
      ),
    [edges, statusByFlowId, selectedNodeIds]
  );

  const graphStructureKey = useMemo(
    () =>
      [
        graph.nodes.map((node) => node.id).join(','),
        graph.edges.map((edge) => edge.id).join(','),
      ].join('|'),
    [graph.nodes, graph.edges]
  );

  const measurementKey = useMemo(() => buildFlowMeasurementKey(nodes), [nodes]);

  useMeasuredFlowLayout({
    graphStructureKey,
    measurementKey,
    layoutEdges: graph.edges,
    setNodes,
  });

  useEffect(() => {
    setNodes(graph.nodes);
  }, [graph.nodes, setNodes]);

  useEffect(() => {
    setEdges(graph.edges);
  }, [graph.edges, setEdges]);

  const layoutKey = useMemo(
    () =>
      nodes
        .map(
          (node) =>
            `${node.id}:${node.position.x.toFixed(0)},${node.position.y.toFixed(
              0
            )}`
        )
        .join('|'),
    [nodes]
  );

  // Selecting a node shows its step in the inspector, so the graph stays on
  // screen — when a run fails you want the error *and* where that step sits.
  const [inspectorStep, setInspectorStep] =
    useState<WorkflowRunInspectorStep | null>(null);
  const revealNode = useRevealNodeBesideInspector();

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const data = node.data as WorkflowRunFlowNodeData | undefined;
      const definitionNodeId = data?.definitionNodeId ?? node.id;
      const instanceKey = data?.instanceKey;
      // `runGraphToFlow` merges the run node's name into its view data (and
      // suffixes fan-out instances with their index), so this is already the label
      // the card itself shows.
      const stepName = readString(asRecord(data?.node.data), 'name');
      const title =
        stepName !== undefined && stepName !== ''
          ? stepName
          : isAgentRunNode(node)
          ? 'Conversation'
          : 'Step';
      setInspectorStep({
        title,
        view: {
          type: WORKFLOW_RUN_STEP_VIEW_TYPE,
          workflowId,
          runId,
          nodeId: definitionNodeId,
          ...(instanceKey !== undefined ? { instanceKey } : {}),
        },
      });
      revealNode(node.id);
    },
    [workflowId, runId, revealNode]
  );

  const closeInspector = useCallback(() => {
    setInspectorStep(null);
  }, []);

  /** The "open full width" action: the same step, as a full-panel stack view. */
  const expandStep = useCallback(
    (step: WorkflowRunInspectorStep) => {
      const { nodeId, instanceKey } = step.view;
      // Push (do NOT replaceTop): the step view stacks on top of the run graph
      // so back navigation returns to the graph, not the runs list.
      artifactPanel?.pushView({
        id:
          instanceKey !== undefined && instanceKey !== 'root'
            ? `workflow-run-step-${nodeId}-${instanceKey}`
            : `workflow-run-step-${nodeId}`,
        title: step.title,
        payload: step.view,
      });
      setInspectorStep(null);
    },
    [artifactPanel]
  );

  return (
    <Canvas
      nodes={nodes}
      edges={renderEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={WORKFLOW_RUN_NODE_TYPES}
      defaultEdgeOptions={WORKFLOW_EDGE_OPTIONS}
      className="h-full w-full"
      proOptions={{ hideAttribution: true }}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={true}
      panOnDrag={true}
      panOnScroll
      selectionOnDrag={false}
      fitView={false}
      fitViewOptions={WORKFLOW_FLOW_FIT_VIEW_OPTIONS}
      onNodeClick={onNodeClick}
    >
      <RunGraphFitView layoutKey={layoutKey} />
      <WorkflowRunGraphInspector
        step={inspectorStep}
        onClose={closeInspector}
        onExpand={expandStep}
      />
    </Canvas>
  );
};

/**
 * Read-only workflow run graph: renders the run's nodes (looking like the
 * editor canvas, via the render catalog) annotated with status; clicking a node
 * pushes its step view onto the artifact-panel stack.
 */
export const WorkflowRunGraph: React.FC = () => {
  const model = useWorkflowRunModel();
  const { workflowId, runId } = model;
  const data = useWatchableValue(model.graphWatchable);

  const graph = useMemo(
    () => (data === null ? null : runGraphToFlow(data.nodes, data.edges)),
    [data]
  );

  if (graph === null) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Text.P className="text-muted-foreground">Loading graph...</Text.P>
      </div>
    );
  }

  if (graph.nodes.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Text.P className="text-muted-foreground">
          No graph recorded for this run yet.
        </Text.P>
      </div>
    );
  }

  return (
    <WorkflowRenderCatalogProvider catalog={DEFAULT_WORKFLOW_RENDER_CATALOG}>
      <div className="h-full w-full min-h-0">
        <ReactFlowProvider>
          <WorkflowRunGraphInner
            workflowId={workflowId}
            runId={runId}
            graph={graph}
          />
        </ReactFlowProvider>
      </div>
    </WorkflowRenderCatalogProvider>
  );
};
